import React, { useState } from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  useTheme,
  Box,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InsightsIcon from "@mui/icons-material/Insights";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import ContactsIcon from "@mui/icons-material/Contacts";
import GroupIcon from "@mui/icons-material/Group";
import AdminIcon from "@mui/icons-material/AdminPanelSettings";
import SecurityIcon from "@mui/icons-material/Security";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import { useNavigate, useLocation } from "react-router-dom";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import SummarizeIcon from "@mui/icons-material/Summarize";

export default function MenuContent({ drawerOpen, onForceOpenDrawer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [contactOpen, setContactOpen] = useState(false);
  const [userMgmtOpen, setUserMgmtOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);

  const toggleSubmenu = (isOpen, setIsOpen) => {
    setIsOpen((prev) => !prev);
  };

  const renderNavItem = ({
    label,
    icon,
    onClick,
    showArrow = false,
    arrowOpen = false,
    path,
    subPaths = [],
  }) => {
    const isActive = path && location.pathname === path;
    const isSubmenuActive = subPaths.some(
      (subPath) => location.pathname === subPath
    );

    const itemButton = (
      <ListItemButton
        onClick={onClick}
        sx={{
          justifyContent: drawerOpen ? "flex-start" : "center",
          px: drawerOpen ? 2 : 1,
          py: 1.5,
          minHeight: 48,
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          position: "relative",
          color:
            isActive || isSubmenuActive
              ? theme.palette.text.secondary
              : theme.palette.text.secondary,
          backgroundColor:
            isActive || isSubmenuActive
              ? theme.palette.primary.light + "25"
              : "transparent",
          "&:hover": {
            backgroundColor:
              isActive || isSubmenuActive
                ? theme.palette.background.buttonGrey
                : theme.palette.background.buttonGrey,
          },
          "& .MuiListItemIcon-root": {
            color:
              isActive || isSubmenuActive
                ? theme.palette.text.secondary
                : "inherit",
            minWidth: drawerOpen ? 40 : "auto",
            justifyContent: "center",
          },
          "& .MuiListItemText-primary": {
            color:
              isActive || isSubmenuActive
                ? theme.palette.text.secondary
                : "inherit",
            fontWeight: isActive || isSubmenuActive ? 600 : 400,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: drawerOpen ? 40 : "auto",
            mr: drawerOpen ? 0 : "auto",
            ml: drawerOpen ? 0 : "auto",
          }}
        >
          {icon}
        </ListItemIcon>
        {drawerOpen && (
          <ListItemText
            primary={label}
            sx={{
              ml: 1,
              "& .MuiTypography-root": {
                fontSize: "0.875rem",
              },
            }}
          />
        )}
        {showArrow && (
          <Box
            sx={{
              position: drawerOpen ? "relative" : "absolute",
              right: drawerOpen ? "auto" : 4,
              ml: drawerOpen ? "auto" : 0,
              display: "flex",
            }}
          >
            {arrowOpen ? (
              <ExpandLess sx={{ fontSize: drawerOpen ? 20 : 16 }} />
            ) : (
              <ExpandMore sx={{ fontSize: drawerOpen ? 20 : 16 }} />
            )}
          </Box>
        )}
      </ListItemButton>
    );

    if (!drawerOpen) {
      return (
        <Tooltip title={label} placement="right">
          {itemButton}
        </Tooltip>
      );
    }
    return itemButton;
  };

  const renderSubMenuItem = ({ label, path, icon }) => {
    const isActive = location.pathname === path;

    const subItemButton = (
      <ListItemButton
        onClick={() => navigate(path)}
        sx={{
          pl: drawerOpen ? 4 : 0,
          pr: drawerOpen ? 2 : 0,
          py: 1,
          minHeight: 40,
          display: "flex",
          justifyContent: drawerOpen ? "flex-start" : "center",
          alignItems: "center",
          mx: 1,
          borderRadius: 1,
          color: isActive
            ? theme.palette.text.secondary
            : theme.palette.text.secondary,
          backgroundColor: isActive
            ? theme.palette.primary.light + "25"
            : "transparent",
          "&:hover": {
            backgroundColor: theme.palette.background.buttonGrey,
          },
          "& .MuiTypography-root": {
            fontSize: "0.875rem",
            fontWeight: isActive ? 600 : 400,
          },
          "& .MuiListItemIcon-root": {
            color: isActive
              ? theme.palette.text.secondary
              : theme.palette.text.secondary,
            minWidth: "auto",
            display: "flex",
            justifyContent: "center",
            width: drawerOpen ? 40 : "100%",
          },
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        {drawerOpen && (
          <ListItemText
            primary={label}
            sx={{
              ml: 1,
              "& .MuiTypography-root": {
                fontSize: "0.875rem",
              },
            }}
          />
        )}
      </ListItemButton>
    );

    if (!drawerOpen) {
      return (
        <Tooltip title={label} placement="right">
          {subItemButton}
        </Tooltip>
      );
    }
    return subItemButton;
  };

  return (
    <List component="nav" sx={{ px: 0 }}>
      {/* Home */}
      {renderNavItem({
        label: "Home",
        icon: <HomeIcon />,
        onClick: () => navigate("/home"),
        path: "/home",
      })}

      {/* Insights */}
      {renderNavItem({
        label: "Insights",
        icon: <InsightsIcon />,
        onClick: () => toggleSubmenu(insightsOpen, setInsightsOpen),
        showArrow: true,
        arrowOpen: insightsOpen,
        path: null,
        subPaths: ["/insights/recorded-contacts", "/insights/reports"], // add more later
      })}

      <Collapse in={insightsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {renderSubMenuItem({
            label: "Recorded Contacts",
            path: "/insights/recorded-contacts",
            icon: <ContactPhoneIcon sx={{ fontSize: 20 }} />,
          })}
          {renderSubMenuItem({
            label: "Reports",
            path: "/insights/reports",
            icon: <SummarizeIcon sx={{ fontSize: 20 }} />,
          })}
        </List>
      </Collapse>

      {/* Playlists */}
      {renderNavItem({
        label: "Playlists",
        // icon: <QueueMusicIcon />,
        icon: <HeadphonesIcon />,
        onClick: () => navigate("/playlists"),
        path: "/playlists",
      })}

      {/* Recorded Calls */}
      {renderNavItem({
        label: "Recorded Calls",
        icon: <PhoneIcon />,
        onClick: () => navigate("/recorded-calls"),
        path: "/recorded-calls",
      })}

      {/* Contact Management */}
      {renderNavItem({
        label: "Contact Management",
        icon: <ContactsIcon />,
        onClick: () => toggleSubmenu(contactOpen, setContactOpen),
        showArrow: true,
        arrowOpen: contactOpen,
        path: null,
        subPaths: ["/recorded-contacts", "/third-party-contacts"],
      })}

      <Collapse in={contactOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {renderSubMenuItem({
            label: "Recorded Contacts",
            path: "/recorded-contacts",
            icon: <ContactPhoneIcon sx={{ fontSize: 20 }} />,
          })}
          {renderSubMenuItem({
            label: "Third Party Contacts",
            path: "/third-party-contacts",
            icon: <PersonIcon sx={{ fontSize: 20 }} />,
          })}
        </List>
      </Collapse>

      {/* User Management */}
      {renderNavItem({
        label: "User Management",
        icon: <AdminIcon />,
        onClick: () => toggleSubmenu(userMgmtOpen, setUserMgmtOpen),
        showArrow: true,
        arrowOpen: userMgmtOpen,
        path: null,
        subPaths: ["/users", "/roles"],
      })}

      <Collapse in={userMgmtOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {renderSubMenuItem({
            label: "Users",
            path: "/users",
            icon: <GroupIcon sx={{ fontSize: 20 }} />,
          })}
          {renderSubMenuItem({
            label: "Roles",
            path: "/roles",
            icon: <SecurityIcon sx={{ fontSize: 20 }} />,
          })}
        </List>
      </Collapse>
    </List>
  );
}
